import React, { useMemo, useState } from 'react';

const deviceIcons = {
  desktop: '💻',
  mobile: '📱',
  tablet: '📟',
  unknown: '❔',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const delta = Date.now() - ts;
  const sec = Math.floor(delta / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

export default function MetricsPanel({ data = {}, surge, initialCollapsed }) {
  const defaultCollapsed =
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
  const [collapsed, setCollapsed] = useState(
    typeof initialCollapsed === 'boolean' ? initialCollapsed : defaultCollapsed,
  );

  // Ensure mobile starts collapsed even if mounted server-side
  React.useEffect(() => {
    if (
      typeof initialCollapsed === 'undefined' &&
      typeof window !== 'undefined' &&
      window.innerWidth <= 768
    ) {
      setCollapsed(true);
    }
  }, [initialCollapsed]);

  const topSources = useMemo(() => {
    const src = data.trafficSources || {};
    return Object.entries(src)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [data.trafficSources]);

  const recentArrivals = useMemo(
    () => (data.lastArrivals || []).slice(0, 6),
    [data.lastArrivals],
  );

  const regions = useMemo(() => {
    const reg = data.regionStats || {};
    return Object.entries(reg)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [data.regionStats]);

  return (
    <div className={`metrics-panel ${collapsed ? 'collapsed' : ''}`}>
      <button
        className="metrics-toggle"
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-label="Toggle metrics"
      >
        {collapsed ? '▲' : '▼'} Metrics
      </button>

      {!collapsed && (
        <div className="metrics-body">
          <div className="metrics-row">
            <Metric label="Live Visitors" value={data.liveCount ?? 0} tone="guest" />
            <Metric label="Unique (24h)" value={data.unique24h ?? 0} tone="guest" />
            <Metric label="All-Time" value={data.uniqueAllTime ?? 0} tone="guest" />
          </div>

          <div className="metrics-section">
            <div className="metrics-label">Top Sources</div>
            <ul className="metrics-list">
              {topSources.length === 0 && <li className="metrics-empty">n/a</li>}
              {topSources.map(([name, count]) => (
                <li key={name}>
                  <span className="pill">{name || 'unknown'}</span>
                  <span className="metric-count">{count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="metrics-section">
            <div className="metrics-label">Recent Arrivals</div>
            <ul className="metrics-list recent">
              {recentArrivals.map((g) => (
                <li key={g.fullId || g.sessionId}>
                  <span className="pill">{g.label || g.sessionId}</span>
                  <span className="metric-sub">
                    {g.referrer || g.source || 'unknown'} · {g.region || 'unk'}
                    {' · '}
                    {deviceIcons[g.device] || deviceIcons.unknown}
                    {g.device ? ` ${g.device}` : ''}
                    {g.at ? ` · ${timeAgo(g.at)}` : ''}
                  </span>
                </li>
              ))}
              {recentArrivals.length === 0 && (
                <li className="metrics-empty">Waiting for traffic…</li>
              )}
            </ul>
          </div>

          {data.playerStats && (
            <div className="metrics-section">
              <div className="metrics-label">Players</div>
              <div className="metrics-row">
                <Metric label="Online" value={data.playerStats.online ?? 0} tone="online" />
                <Metric label="Idle" value={data.playerStats.idle ?? 0} tone="idle" />
                <Metric label="Offline (recent)" value={data.playerStats.offline ?? 0} tone="offline" />
              </div>
            </div>
          )}

          <div className="metrics-section">
            <div className="metrics-label">Regions</div>
            <div className="metrics-row">
              {regions.length === 0 && (
                <div className="metrics-empty">n/a</div>
              )}
              {regions.map(([region, count]) => (
                <div key={region} className="metric-chip">
                  {region}: {count}
                </div>
              ))}
            </div>
          </div>

          {surge && (
            <div className="metrics-surge">
              Surge detected · Level {surge.level || 1} · Live{' '}
              {surge.liveCount ?? data.liveCount ?? '—'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  const toneClass = tone ? `tone-${tone}` : '';
  return (
    <div className={`metric ${toneClass}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}
